import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Users.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const ColorsList = forwardRef(({ onSelectColor, selectedColorId, onNew }, ref) => {
  const { t } = useTranslation('admin');
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchColors
  }));

  useEffect(() => {
    fetchColors();
  }, [statusFilter]);

  const fetchColors = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/colors');
      if (statusFilter !== 'all') {
        url += `?is_active=${statusFilter === 'active'}`;
      }

      const response = await apiFetch(url);

      if (response.ok) {
        const data = await response.json();
        setColors(data.colors);
      } else {
        setError(t('colors.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
      setError(t('colors.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredColors = colors.filter(color => {
    const matchesSearch = (
      color.color_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (color.hex_value && color.hex_value.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (color.pantone_code && color.pantone_code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return matchesSearch;
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('colors.filters.status.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('colors.filters.status.inactive')}</span>
    );
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && colors.length === 0) {
    return <div className="list-container"><div className="loading">{t('colors.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('colors.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('colors.newColor')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('colors.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('colors.filters.allStatus')}</option>
            <option value="active">{t('colors.filters.status.active')}</option>
            <option value="inactive">{t('colors.filters.status.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('colors.tableHeaders.name')}</th>
              <th>{t('colors.tableHeaders.hex')}</th>
              <th>{t('colors.tableHeaders.pantone')}</th>
              <th>{t('colors.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredColors.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">
                  {searchTerm ? t('colors.noData.searching') : t('colors.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredColors.map(color => (
                <tr
                  key={color.color_id}
                  onClick={() => onSelectColor(color.color_id)}
                  className={selectedColorId === color.color_id ? 'selected' : ''}
                >
                  <td>
                    <strong>{color.color_name}</strong>
                  </td>
                  <td>
                    <span className="color-swatch" style={{ backgroundColor: color.hex_value || '#ccc', display: 'inline-block', width: '16px', height: '16px', borderRadius: '3px', marginRight: '6px', verticalAlign: 'middle', border: '1px solid rgba(0,0,0,0.2)' }}></span>
                    {color.hex_value || '-'}
                  </td>
                  <td>
                    {color.pantone_code || '-'}
                  </td>
                  <td>
                    {getStatusBadge(color.is_active)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default ColorsList;
